import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  User,
  Edit3,
  Settings,
  Heart,
  MessageCircle,
  Eye,
  Calendar,
  MapPin,
  School,
  Trophy,
  Star,
  ThumbsUp,
  Bookmark,
  Clock,
  TrendingUp,
  Shield,
  Bell,
  Lock,
  Camera,
  Mail,
  Phone,
  LogOut,
  MoreHorizontal,
  Grid3X3,
  List,
  Filter
} from 'lucide-react'
import { useToast } from '@/components/ui/toast-provider'
import { useAuthStore } from '@/stores/auth-store'
import { useContentStore } from '@/stores/content-store'
import { PostCard } from '@/components/home/post-card'
import { PersonalSettings } from '@/components/profile/personal-settings'
import { PageTransition, EmptyState } from '@/components/ui/ux-enhancer'

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user, updateProfile, logout } = useAuthStore()
  const { posts, fetchPosts, isLoading } = useContentStore()
  const { addToast } = useToast()
  
  const [activeTab, setActiveTab] = useState('posts')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    nickname: user?.nickname || user?.username || '',
    bio: user?.bio || '',
    avatar: user?.avatar || ''
  })

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  // 时间格式化函数
  function formatTime(dateString: string): string {
    const now = new Date()
    const postTime = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '刚刚'
    if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}小时前`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}天前`
    
    return postTime.toLocaleDateString('zh-CN')
  }

  // 获取用户发布的帖子
  const userPosts = posts
    .filter(post => post.user?.id === user?.id)
    .map(post => ({
      id: post.id,
      userId: post.userId,
      author: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
        school: post.school,
        level: post.user?.level || 1
      },
      user: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
        school: post.school,
        level: post.user?.level || 1
      },
      content: post.content,
      title: post.title,
      images: post.images || [],
      category: post.category,
      createdAt: new Date(post.createdAt),
      time: formatTime(post.createdAt),
      likes: post.likes || 0,
      comments: post.comments || 0,
      location: post.location,
      price: post.price,
      condition: post.condition,
      tags: post.tags || []
    }))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  // 获取用户点赞的帖子
  const likedPosts = posts
    .filter(post => post.likedBy?.includes(user?.id || ''))
    .map(post => ({
      id: post.id,
      userId: post.userId,
      author: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '/placeholder.svg?height=40&width=40&text=匿名' : (post.user?.avatar || '/placeholder.svg?height=40&width=40'),
        school: post.school,
        level: post.user?.level || 1
      },
      user: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '/placeholder.svg?height=40&width=40&text=匿名' : (post.user?.avatar || '/placeholder.svg?height=40&width=40'),
        school: post.school,
        level: post.user?.level || 1
      },
      content: post.content,
      title: post.title,
      images: post.images || [],
      category: post.category,
      createdAt: new Date(post.createdAt),
      time: formatTime(post.createdAt),
      likes: post.likes || 0,
      comments: post.comments || 0,
      location: post.location,
      price: post.price,
      condition: post.condition,
      tags: post.tags || []
    }))

  // 处理个人资料更新
  const handleUpdateProfile = async () => {
    try {
      await updateProfile(editForm)
      setIsEditing(false)
      addToast({
        type: 'success',
        title: '更新成功',
        description: '个人资料已更新'
      })
    } catch (error) {
      addToast({
        type: 'error',
        title: '更新失败',
        description: '请稍后重试'
      })
    }
  }

  // 处理退出登录
  const handleLogout = () => {
    logout()
    navigate('/login')
    addToast({
      type: 'info',
      title: '已退出登录',
      description: '感谢使用大悟万能墙'
    })
  }

  if (!user) {
    navigate('/login')
    return null
  }

  return (
    <PageTransition>
      <div className="space-y-6">
        {/* 个人资料卡片 */}
        <Card className="overflow-hidden">
          <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600 relative">
            <div className="absolute inset-0 bg-black/20" />
          </div>
          <CardContent className="relative pt-0 pb-6">
            {/* 头像和基本信息 */}
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-16 relative z-10">
              <div className="relative">
                <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                  <AvatarImage src={user.avatar} alt={user.username} />
                  <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                    {user.username?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Button
                  size="sm"
                  className="absolute bottom-0 right-0 rounded-full w-8 h-8 p-0"
                  onClick={() => setIsEditing(true)}
                >
                  <Camera className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      {user.username}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Dialog open={isEditing} onOpenChange={setIsEditing}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Edit3 className="w-4 h-4 mr-2" />
                          编辑资料
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>编辑个人资料</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="nickname">昵称</Label>
                            <Input
                              id="nickname"
                              value={editForm.nickname}
                              onChange={(e) => setEditForm(prev => ({ ...prev, nickname: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label htmlFor="bio">个人简介</Label>
                            <Textarea
                              id="bio"
                              value={editForm.bio}
                              onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                              placeholder="介绍一下自己..."
                              rows={3}
                            />
                          </div>
                          <div className="flex gap-2 pt-4">
                            <Button onClick={handleUpdateProfile} className="flex-1">
                              保存
                            </Button>
                            <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1">
                              取消
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Button variant="outline" size="sm" onClick={handleLogout}>
                      <LogOut className="w-4 h-4 mr-2" />
                      退出
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* 个人简介 */}
            {user.bio && (
              <div className="mt-4">
                <p className="text-gray-700">{user.bio}</p>
              </div>
            )}

            {/* 统计数据 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{userPosts.length}</div>
                <div className="text-sm text-gray-600">发布内容</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">
                  {userPosts.reduce((sum, post) => sum + post.likes, 0)}
                </div>
                <div className="text-sm text-gray-600">获得点赞</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">
                  {userPosts.reduce((sum, post) => sum + post.comments, 0)}
                </div>
                <div className="text-sm text-gray-600">收到评论</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-500">{likedPosts.length}</div>
                <div className="text-sm text-gray-600">点赞内容</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 内容标签页 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="posts" className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    我的发布 ({userPosts.length})
                  </TabsTrigger>
                  <TabsTrigger value="liked" className="flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    我的点赞 ({likedPosts.length})
                  </TabsTrigger>
                  <TabsTrigger value="settings" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    设置
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {(activeTab === 'posts' || activeTab === 'liked') && (
                <div className="flex gap-2 ml-4">
                  <Button
                    variant={viewMode === 'list' ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'grid' ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* 我的发布 */}
              <TabsContent value="posts" className="space-y-4">
                {userPosts.length > 0 ? (
                  <div className={`space-y-4 ${viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}`}>
                    {userPosts.map(post => (
                      <PostCard key={post.id} post={post} />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={<MessageCircle className="w-16 h-16 text-gray-300" />}
                    title="还没有发布内容"
                    description="开始分享你的校园生活吧"
                    action={
                      <button
                        onClick={() => navigate('/publish')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        发布内容
                      </button>
                    }
                  />
                )}
              </TabsContent>

              {/* 我的点赞 */}
              <TabsContent value="liked" className="space-y-4">
                {likedPosts.length > 0 ? (
                  <div className={`space-y-4 ${viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}`}>
                    {likedPosts.map(post => (
                      <PostCard key={post.id} post={post} />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Heart className="w-16 h-16 text-gray-300" />}
                    title="还没有点赞内容"
                    description="去发现一些有趣的内容吧"
                    action={
                      <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        浏览内容
                      </button>
                    }
                  />
                )}
              </TabsContent>

              {/* 设置 */}
              <TabsContent value="settings">
                <PersonalSettings />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  )
}